import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { FilePlus2, MapPin, Send } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'

import { InlineFieldInput, InlineFieldTextarea } from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { newRequestId } from '@/features/quote-builder/publishing/api'
import { blankAddress, addressSummary } from '@/features/quote-builder/start/address'
import { AddressFields } from '@/features/quote-builder/start/address-panel'
import { CustomerSearch } from '@/features/quote-builder/start/customer-search'
import { formatMoney } from '@/lib/utils'

import {
  newInvoiceDefaultsQueryOptions,
  newInvoiceErrorMessage,
  newInvoiceTaxQueryOptions,
  receivablesApi,
  useReceivablesApi,
  type Address,
  type NewInvoiceSource,
} from './api'
import { blankLine, lineCents, lineValid, moneyCents, type Line } from './invoice-lines'
import { InvoiceLinesEditor } from './invoice-lines-editor'

const TERMS = [
  { value: '0', label: 'Due on receipt' },
  { value: '15', label: 'Net 15' },
  { value: '30', label: 'Net 30' },
] as const
type Terms = (typeof TERMS)[number]['value']

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Cut an invoice for extra items, updated pricing or a one-off charge (USA-378): from a project or
 * quote (the customer and email come with it), a customer's page, or AR Invoices (pick the customer).
 * Without a quote, tax follows the ship-to, which starts at the customer's address (USA-1543).
 */
export function NewInvoiceDialog({
  source,
  trigger,
  openAfterCreate = false,
}: {
  source: NewInvoiceSource
  trigger?: ReactNode
  /** Go to the new invoice's page (AR Invoices); otherwise stay where it was cut. */
  openAfterCreate?: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm" variant="outline">
            <FilePlus2 aria-hidden="true" />
            New invoice
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        {open ? <NewInvoiceForm onDone={() => setOpen(false)} openAfterCreate={openAfterCreate} source={source} /> : null}
      </DialogContent>
    </Dialog>
  )
}

function NewInvoiceForm({
  source,
  onDone,
  openAfterCreate,
}: {
  source: NewInvoiceSource
  onDone: () => void
  openAfterCreate: boolean
}) {
  const api = useReceivablesApi()
  const client = useQueryClient()
  const navigate = useNavigate()
  const [customerId, setCustomerId] = useState<number | undefined>(source.customerGroupId)
  const fromContext = Boolean(source.projectUuid || source.quoteId || source.customerGroupId != null)
  const defaults = useQuery({
    ...newInvoiceDefaultsQueryOptions(api, { ...source, customerGroupId: customerId }),
    enabled: fromContext || customerId != null,
  })
  const [email, setEmail] = useState<string | null>(null)
  const [lines, setLines] = useState<Line[]>(() => [blankLine()])
  const [terms, setTerms] = useState<Terms>('0')
  const [memo, setMemo] = useState('')
  const [shipping, setShipping] = useState('')
  const [taxExempt, setTaxExempt] = useState(false)
  // The ship-to staff typed; null keeps the customer's address on file.
  const [shipTo, setShipTo] = useState<Address | null>(null)
  const [editingShipTo, setEditingShipTo] = useState(false)
  const [send, setSend] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  // One id per invoice: a retry after an error finishes the same invoice instead of creating another.
  const [requestId] = useState(newRequestId)

  const info = defaults.data
  const withoutQuote = info != null && info.quote == null
  const typedTax = useQuery({
    ...newInvoiceTaxQueryOptions(api, shipTo ?? blankAddress()),
    enabled: withoutQuote && shipTo != null,
  })
  const recipient = (email ?? info?.recipient_email ?? '').trim()
  const subtotalCents = lines.reduce((sum, line) => sum + (lineCents(line) ?? 0), 0)
  const shippingCents = shipping.trim() ? moneyCents(shipping) : 0
  const tax = shipTo != null ? (typedTax.data ?? info?.tax) : info?.tax
  const exempt = taxExempt || tax?.status === 'exempt'
  // A preview: the server works out the tax the same way when the invoice is created.
  const taxCents =
    exempt || !tax || tax.status !== 'ready'
      ? 0
      : Math.round(
          ((tax.items_taxable ? Math.max(subtotalCents, 0) : 0) +
            (tax.shipping_taxable ? (shippingCents ?? 0) : 0)) *
            Number(tax.rate) /
            100,
        )
  const totalCents = subtotalCents + (shippingCents ?? 0) + taxCents
  const errors = {
    email: !EMAIL.test(recipient),
    lines: lines.some((line) => !lineValid(line)),
    total: subtotalCents <= 0,
    shipping: shippingCents === null,
    tax: !exempt && tax?.status === 'review',
  }
  const ready =
    info?.customer_group_id != null &&
    !errors.email &&
    !errors.lines &&
    !errors.total &&
    !errors.shipping &&
    !errors.tax &&
    !(shipTo != null && typedTax.isFetching)

  const create = useMutation({
    retry: false,
    mutationFn: () =>
      receivablesApi.create(api, {
        request_id: requestId,
        quote_id: info?.quote?.id ?? null,
        project_uuid: info?.project?.uuid ?? null,
        customer_group_id: info?.customer_group_id ?? null,
        recipient_email: recipient,
        lines: lines.map((line) => ({
          description: line.description.trim(),
          quantity: line.quantity.trim(),
          unit_price: line.unitPrice.trim(),
        })),
        due_days: Number(terms),
        memo: memo.trim() || null,
        shipping: shipping.trim() ? Number(shipping).toFixed(2) : '0',
        tax_exempt: taxExempt,
        ship_to: withoutQuote ? shipTo : null,
        send,
      }),
    onSuccess: (invoice) => {
      void client.invalidateQueries({ queryKey: ['receivables'] })
      void client.invalidateQueries({ queryKey: ['projects', 'payments'] })
      void client.invalidateQueries({ queryKey: ['quote-invoicing'] })
      void client.invalidateQueries({ queryKey: ['customer-payments'] })
      toast.success(
        invoice.sent_at
          ? `Invoice ${invoice.invoice_number} sent to ${invoice.recipient_email}`
          : `Invoice ${invoice.invoice_number} created`,
      )
      onDone()
      if (openAfterCreate) void navigate({ to: '/receivables/$invoiceId', params: { invoiceId: invoice.invoice_id } })
    },
  })

  const submit = () => {
    setSubmitted(true)
    if (ready && !create.isPending) create.mutate()
  }

  if (!fromContext && customerId == null) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>New invoice</DialogTitle>
        </DialogHeader>
        <CustomerSearch onSelect={(customer) => setCustomerId(customer.id)} />
      </>
    )
  }

  const linked = [info?.project?.name, info?.quote?.quote_number].filter(Boolean)
  return (
    <form
      className="grid gap-5"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <DialogHeader>
        <DialogTitle>New invoice</DialogTitle>
        <p className="flex min-h-5 flex-wrap items-center justify-center gap-x-2 text-sm text-muted-foreground sm:justify-start">
          {info ? (
            <>
              <span className="font-medium text-foreground">{info.customer_name || 'No customer'}</span>
              {linked.map((item) => (
                <span key={item}>· {item}</span>
              ))}
              {!fromContext ? (
                <Button className="h-auto px-1 py-0" onClick={() => setCustomerId(undefined)} size="sm" type="button" variant="link">
                  Change
                </Button>
              ) : null}
            </>
          ) : defaults.isError ? (
            <span className="text-destructive">The customer could not be loaded. Close and try again.</span>
          ) : (
            'Loading customer…'
          )}
        </p>
      </DialogHeader>

      {info && info.customer_group_id == null ? (
        <p className="text-sm text-destructive" role="alert">
          This project has no customer. Add one to the project first.
        </p>
      ) : null}

      <div className="grid gap-1.5">
        <Label htmlFor="new-invoice-email">Email to</Label>
        <InlineFieldInput
          aria-invalid={submitted && errors.email}
          autoComplete="off"
          id="new-invoice-email"
          inputMode="email"
          onChange={(event) => setEmail(event.target.value)}
          placeholder="customer@company.com"
          value={email ?? info?.recipient_email ?? ''}
        />
        {submitted && errors.email ? <p className="text-xs text-destructive">Enter the customer’s email.</p> : null}
      </div>

      {withoutQuote ? (
        <ShipTo
          address={shipTo ?? info.ship_to ?? null}
          editing={editingShipTo}
          onChange={setShipTo}
          onEdit={() => {
            setShipTo((current) => current ?? info.ship_to ?? { ...blankAddress(), name: info.customer_name })
            setEditingShipTo(true)
          }}
        />
      ) : null}

      <div className="grid gap-2">
        <InvoiceLinesEditor
          lines={lines}
          onChange={setLines}
          poItems={info?.po_items ?? []}
          submitted={submitted}
          totalLabel="Subtotal"
        />
        {submitted && (errors.lines || errors.total) ? (
          <p className="text-xs text-destructive">
            {errors.lines ? 'Every item needs a description, a quantity and a price.' : 'The total must be more than zero.'}
          </p>
        ) : null}
      </div>

      <dl className="ml-auto grid w-full max-w-sm gap-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <dt>
            <Label htmlFor="new-invoice-shipping">Shipping</Label>
          </dt>
          <dd>
            <InlineFieldInput
              aria-invalid={submitted && errors.shipping}
              className="h-8 w-32 text-right tabular-nums"
              id="new-invoice-shipping"
              inputMode="decimal"
              onChange={(event) => setShipping(event.target.value)}
              placeholder="0.00"
              value={shipping}
            />
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="min-w-0">
            Tax
            {tax?.status === 'ready' && !exempt ? (
              <span className="ml-1.5 text-muted-foreground">
                {[tax.jurisdiction, `${Number(tax.rate)}%`].filter(Boolean).join(' · ')}
              </span>
            ) : null}
          </dt>
          <dd className="flex items-center gap-3">
            {tax?.status !== 'exempt' ? (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground" htmlFor="new-invoice-tax-exempt">
                <Switch checked={taxExempt} id="new-invoice-tax-exempt" onCheckedChange={setTaxExempt} />
                Exempt
              </label>
            ) : null}
            <span className="w-20 text-right tabular-nums">{formatMoney(taxCents / 100)}</span>
          </dd>
        </div>
        {tax?.status === 'review' && !exempt ? (
          <p className={submitted ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>{tax.message}</p>
        ) : null}
        {tax?.status === 'exempt' ? <p className="text-xs text-muted-foreground">{tax.message}</p> : null}
        <div className="flex justify-between gap-3 border-t pt-2 font-semibold">
          <dt>Total</dt>
          <dd className="tabular-nums">{formatMoney(totalCents / 100)}</dd>
        </div>
      </dl>

      <div className="grid gap-4 sm:grid-cols-[auto_minmax(0,1fr)]">
        <div className="grid content-start gap-1.5">
          <span className="text-sm font-medium">Terms</span>
          <ReviewChoiceGroup label="Terms" onChange={setTerms} options={TERMS} value={terms} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="new-invoice-memo">Memo</Label>
          <InlineFieldTextarea
            className="min-h-9"
            id="new-invoice-memo"
            maxLength={1000}
            onChange={(event) => setMemo(event.target.value)}
            placeholder="Optional"
            rows={1}
            value={memo}
          />
        </div>
      </div>

      {create.isError ? (
        <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert">
          {newInvoiceErrorMessage(create.error)}
        </p>
      ) : null}

      <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label className="flex items-center gap-2 text-sm" htmlFor="new-invoice-send">
          <Switch checked={send} id="new-invoice-send" onCheckedChange={setSend} />
          Email it now
        </label>
        <div className="flex gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
          <Button onClick={onDone} type="button" variant="outline">
            Cancel
          </Button>
          <LoadingButton disabled={!info || info.customer_group_id == null} loading={create.isPending} loadingText="Creating..." type="submit">
            {send ? <Send aria-hidden="true" /> : <FilePlus2 aria-hidden="true" />}
            {send ? 'Create and send' : 'Create invoice'}
          </LoadingButton>
        </div>
      </DialogFooter>
    </form>
  )
}

/** Where the items go: taxed by its ZIP and printed on the invoice. One line until staff change it. */
function ShipTo({
  address,
  editing,
  onChange,
  onEdit,
}: {
  address: Address | null
  editing: boolean
  onChange: (value: Address) => void
  onEdit: () => void
}) {
  const known = address != null && (address.line1.trim() !== '' || address.postal_code.trim() !== '')
  if (editing && address) {
    return (
      <fieldset className="grid gap-1.5">
        <legend className="mb-1.5 text-sm font-medium">Ship to</legend>
        <AddressFields onChange={onChange} streetOnly value={address} />
      </fieldset>
    )
  }
  return (
    <div className="grid gap-1.5">
      <span className="text-sm font-medium">Ship to</span>
      <div className="flex min-h-9 items-center gap-2 text-sm">
        <MapPin aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        <span className={known ? 'min-w-0 flex-1 truncate' : 'min-w-0 flex-1 text-muted-foreground'}>
          {known ? [addressSummary(address), address.postal_code].filter(Boolean).join(' ') : 'No address on file'}
        </span>
        <Button className="h-auto shrink-0 px-1 py-0" onClick={onEdit} size="sm" type="button" variant="link">
          {known ? 'Change' : 'Add address'}
        </Button>
      </div>
    </div>
  )
}
